import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Student, Payment, StudentPaymentStatus, PaymentStatus } from '@/lib/types';

export interface FinancialSummary {
  projected: number;
  received: number;
  pendingAmount: number;
}

interface SelectedMonth {
  year: number;
  month: number; // 0-indexed (January = 0)
}

function computeStatus(
  student: Student,
  currentMonthPayment: Payment | null,
  refDate: Date,
): PaymentStatus {
  if (!student.monthly_fee) return 'no_fee';
  if (currentMonthPayment) return 'paid';
  const dueDay = student.due_day ?? 10;
  if (refDate.getDate() >= dueDay) return 'overdue';
  return 'pending';
}

const STATUS_ORDER: Record<PaymentStatus, number> = {
  overdue: 0,
  pending: 1,
  paid: 2,
  no_fee: 3,
};

export function useFinancialOverview(
  personalId: string,
  selectedMonth?: SelectedMonth,
) {
  const now = new Date();
  const year = selectedMonth?.year ?? now.getFullYear();
  const month = selectedMonth?.month ?? now.getMonth();

  const { data, isLoading: loading } = useQuery({
    queryKey: ['financial-overview', personalId, year, month],
    queryFn: async () => {
      const monthStart = new Date(year, month, 1).toISOString();
      const monthEnd = new Date(year, month + 1, 0, 23, 59, 59).toISOString();

      // Use last day of selected month as reference for past months
      // (past months: anyone who didn't pay is overdue)
      const isCurrentMonth =
        year === now.getFullYear() && month === now.getMonth();
      const refDate = isCurrentMonth
        ? now
        : new Date(year, month + 1, 0); // last day of selected month

      const [studentsRes, thisMonthRes] = await Promise.all([
        supabase
          .from('students')
          .select('*')
          .eq('personal_id', personalId)
          .eq('status', 'active'),
        supabase
          .from('payments')
          .select('*')
          .eq('personal_id', personalId)
          .gte('paid_at', monthStart)
          .lte('paid_at', monthEnd),
      ]);

      if (studentsRes.error) throw studentsRes.error;
      if (thisMonthRes.error) throw thisMonthRes.error;

      const students = (studentsRes.data as Student[]) ?? [];
      const thisMonthPayments = (thisMonthRes.data as Payment[]) ?? [];

      const byStudentThisMonth = new Map<string, Payment>();
      for (const p of thisMonthPayments) byStudentThisMonth.set(p.student_id, p);

      const result: StudentPaymentStatus[] = students.map((student) => {
        const currentMonthPayment = byStudentThisMonth.get(student.id) ?? null;
        return {
          student,
          status: computeStatus(student, currentMonthPayment, refDate),
          lastPayment: null,
          currentMonthPayment,
        };
      });

      result.sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);

      const projected = students.reduce(
        (sum, s) => sum + (s.monthly_fee ?? 0),
        0,
      );
      const received = thisMonthPayments.reduce((sum, p) => sum + p.amount, 0);
      const pendingAmount = result
        .filter((o) => o.status === 'pending')
        .reduce((sum, o) => sum + (o.student.monthly_fee ?? 0), 0);

      return { overview: result, projected, received, pendingAmount };
    },
    enabled: !!personalId,
  });

  return {
    overview: data?.overview ?? [],
    projected: data?.projected ?? 0,
    received: data?.received ?? 0,
    pendingAmount: data?.pendingAmount ?? 0,
    loading,
  };
}
